import React, { useEffect, useMemo, useRef, useState } from 'react';
import { NavLink, useParams } from 'react-router-dom';
import { useCatalog } from '../app/providers/CatalogProvider.jsx';
import { useSession } from '../app/providers/SessionProvider.jsx';
import { useConsent } from '../app/providers/ConsentProvider.jsx';
import { useAccess } from '../app/providers/AccessProvider.jsx';
import { accessMessage } from '../services/access/entitlementService.js';
import {
  adTagCandidates,
  fetchAdConfig,
  midrollBreakpoints,
} from '../services/ads/adConfigService.js';
import { createImaController } from '../services/ads/imaService.js';
import { mountGamDisplay } from '../services/ads/gamDisplayService.js';
import { runFixedMidrollPod } from '../services/ads/midrollPodService.js';
import { clearCloudProgress, saveCloudProgress } from '../services/playback/cloudProgressService.js';
import {
  clearPlaybackProgress,
  readPlaybackProgress,
  resolveEpisodePlayback,
  resolveTitlePlayback,
  savePlaybackProgress,
  viewerProgressScope,
} from '../services/playback/playbackService.js';
import '../styles/playback.css';

const MuxPlayer = React.lazy(() => import('@mux/mux-player-react'));
const SAVE_INTERVAL_SECONDS = 5;
const CLOUD_SAVE_INTERVAL_SECONDS = 15;
const MIDROLL_SLOT_COUNT = 3;
const sleep = (ms) => new Promise((resolve) => window.setTimeout(resolve, ms));
const DEFAULT_TIMELINE_TRACK = 'rgb(255 255 255 / .24)';

function midrollMarkerBackground(points, durationSeconds) {
  const duration = Number(durationSeconds) || 0;
  const base = `linear-gradient(90deg, ${DEFAULT_TIMELINE_TRACK} 0%, ${DEFAULT_TIMELINE_TRACK} 100%)`;
  if (!duration || !Array.isArray(points) || !points.length) return base;

  const markers = points
    .map((point) => Math.min(100, Math.max(0, (Number(point) / duration) * 100)))
    .filter((percent) => Number.isFinite(percent))
    .map((percent) => {
      const at = Number(percent.toFixed(4));
      return `linear-gradient(90deg, transparent calc(${at}% - 2px), #d4af37 calc(${at}% - 2px), #d4af37 calc(${at}% + 2px), transparent calc(${at}% + 2px))`;
    });
  return [...markers, base].join(', ');
}

function formatCueTime(seconds) {
  const total = Math.max(0, Math.round(Number(seconds) || 0));
  const minutes = Math.floor(total / 60);
  return `${minutes}:${String(total % 60).padStart(2, '0')}`;
}

function formatAdCountdown(seconds) {
  const total = Math.max(0, Math.ceil(Number(seconds) || 0));
  const minutes = Math.floor(total / 60);
  return `${minutes}:${String(total % 60).padStart(2, '0')}`;
}

function mediaDurationHint(item) {
  const raw = item?.raw && typeof item.raw === 'object' ? item.raw : (item || {});
  const seconds = [
    item?.durationSeconds,
    item?.runtimeSeconds,
    raw?.durationSeconds,
    raw?.duration_seconds,
    raw?.runtimeSeconds,
    raw?.runtime_seconds,
  ].map(Number).find((value) => Number.isFinite(value) && value > 0);
  if (seconds) return seconds;
  const minutes = [
    item?.durationMinutes,
    item?.runtimeMinutes,
    raw?.durationMinutes,
    raw?.duration_minutes,
    raw?.runtimeMinutes,
    raw?.runtime_minutes,
  ].map(Number).find((value) => Number.isFinite(value) && value > 0);
  return minutes ? minutes * 60 : 0;
}

function findTitle(catalog, id) {
  return catalog?.titles?.find((item) => item.id === id || item.slug === id) || null;
}

function findEpisode(series, season, episode) {
  return series?.episodes?.find((entry) => String(entry.season) === String(season) && String(entry.episode) === String(episode)) || null;
}

function PlaybackState({ children }) {
  const { status, error, retry } = useCatalog();
  if (status === 'loading') return <section className="playback-state" aria-busy="true"><p>Preparing playback…</p></section>;
  if (status === 'error') {
    return (
      <section className="playback-state playback-error">
        <h1>Playback is unavailable.</h1>
        <p>{error?.message || 'The WatchVIM catalog could not be loaded.'}</p>
        <button type="button" onClick={retry}>Try Again</button>
      </section>
    );
  }
  return children;
}

function AccessBlocked({ decision, title, backTo, accessError }) {
  const { user } = useSession();
  const reason = accessError && user ? 'verification_error' : decision?.reason;
  const message = reason === 'verification_error'
    ? 'WatchVIM could not verify this account’s current access. Refresh your account status before playback.'
    : accessMessage(decision, title);
  const showLogin = !user && ['login', 'login_or_choose', 'tvod'].includes(decision?.reason);
  const showSubscribe = ['login', 'login_or_choose', 'upgrade', 'svod_or_rent', 'episode_or_season_rent'].includes(decision?.reason) || reason === 'verification_error';
  const showRent = ['login_or_choose', 'svod_or_rent', 'tvod', 'episode_or_season_rent'].includes(decision?.reason);

  return (
    <section className="playback-page playback-access-blocked" data-access-reason={reason || 'blocked'}>
      <div className="playback-access-card">
        <span className="eyebrow">WatchVIM Access</span>
        <h1>{title}</h1>
        <p>{message}</p>
        <div className="playback-access-actions">
          {showLogin ? <NavLink className="hero-button primary" to={`/login?return=${encodeURIComponent(window.location.pathname)}`}>Log In</NavLink> : null}
          {showSubscribe ? <NavLink className="hero-button primary" to="/membership">Subscribe</NavLink> : null}
          {showRent ? <NavLink className="hero-button secondary" to={backTo}>View Rental Options</NavLink> : null}
          <NavLink className="hero-button secondary" to={backTo}>Back</NavLink>
        </div>
      </div>
    </section>
  );
}

// Only titles explicitly prepared for protected AVOD delivery use this gate.
// The server resolves paid access before considering country restrictions.
function ProtectedPlayback({ item, parent, ...props }) {
  const { session } = useSession();
  const policy = (item?.raw || item)?.avodGeo ?? (parent?.raw || parent)?.avodGeo;
  const protectedContent = props.media.kind !== 'trailer' && Boolean(policy && (policy.protectedPlayback === true || (policy.mode && policy.mode !== 'worldwide')));
  const requestKey = `${props.media.mediaKey}:${props.decision.adMode}:${session?.access_token || ''}`;
  const [authorization, setAuthorization] = useState(null);
  const [attempt, setAttempt] = useState(0);
  useEffect(() => {
    if (!protectedContent) return undefined;
    const controller = new AbortController();
    let active = true;
    setAuthorization(null);
    const timer = setTimeout(() => controller.abort(), 20000);
    fetch('/api/playback/session', {
      method: 'POST', cache: 'no-store', signal: controller.signal,
      headers: { 'content-type': 'application/json', ...(session?.access_token ? { authorization: `Bearer ${session.access_token}` } : {}) },
      body: JSON.stringify({ titleId: parent?.id || item?.id, ...(parent ? { season: item.season, episode: item.episode } : {}) }),
    }).then(async response => {
      const payload = await response.json();
      if (!response.ok || payload.allowed !== true) throw new Error(payload.error || 'Playback is unavailable.');
      if (active) setAuthorization({ requestKey, payload });
    }).catch(error => {
      if (!active) return;
      if (!controller.signal.aborted) setAuthorization({ requestKey, error: error.message });
      else if (error.name === 'AbortError') setAuthorization({ requestKey, error: 'Playback verification timed out. Please try again.' });
    }).finally(() => clearTimeout(timer));
    return () => { active = false; clearTimeout(timer); controller.abort(); };
  }, [protectedContent, requestKey, attempt]);
  if (!protectedContent) return <PlaybackSurface {...props} />;
  if (!authorization || authorization.requestKey !== requestKey) return <section className="playback-state" aria-busy="true"><p>Preparing playback…</p></section>;
  if (authorization.error) return <section className="playback-state playback-error" data-avod-geo="blocked">
    <h1>{props.media.title}</h1><p>{authorization.error}</p>
    <button type="button" onClick={() => setAttempt(value => value + 1)}>Try Again</button>
    <NavLink to={props.backTo}>Back to title</NavLink>
  </section>;
  const authorized = authorization.payload;
  return <PlaybackSurface {...props} media={{ ...props.media, playbackId: authorized.playbackId, tokens: authorized.tokens }} decision={{ ...props.decision, adMode: authorized.adMode }} />;
}

function CompanionAd({ adsense, placements, titleId }) {
  const hostRef = useRef(null);
  const enabled = adsense?.enabled === true;
  const client = String(adsense?.client || '').trim();
  const slot = String(adsense?.watchCompanionSlot || '').trim();
  const adUnitPath = String(adsense?.adUnitPath || '').trim();
  const refreshDeclared = adsense?.refreshDeclared === true;
  const placement = useMemo(() => {
    const eligible = (Array.isArray(placements) ? placements : []).filter((item) => (
      item?.imageUrl && (item.placementScope !== 'title' || String(item.targetTitleId || '') === String(titleId || ''))
    ));
    return eligible.find((item) => item.placementScope === 'title') || eligible[0] || null;
  }, [placements, titleId]);
  const [delivery, setDelivery] = useState(enabled && (adUnitPath || (client && slot)) ? 'loading' : (placement ? 'fallback' : 'empty'));

  useEffect(() => {
    const host = hostRef.current;
    if (!host) return undefined;
    host.replaceChildren();

    if (enabled && adUnitPath) {
      setDelivery('loading');
      return mountGamDisplay({ host, adUnitPath, refreshDeclared, onStatus: (status) => {
        setDelivery(status === 'unfilled' || status === 'error' ? (placement ? 'fallback' : 'empty') : status);
      } });
    }

    if (!enabled || !client || !slot) {
      setDelivery(placement ? 'fallback' : 'empty');
      return undefined;
    }

    setDelivery('loading');
    const ins = document.createElement('ins');
    ins.className = 'adsbygoogle watchvim-adsense-companion';
    ins.style.display = 'block';
    ins.dataset.adClient = client;
    ins.dataset.adSlot = slot;
    ins.dataset.adFormat = 'auto';
    ins.dataset.fullWidthResponsive = 'true';
    ins.dataset.watchvimCompanion = 'true';
    host.replaceChildren(ins);

    const applyStatus = () => {
      const status = String(ins.dataset.adStatus || '').toLowerCase();
      if (status === 'filled') {
        setDelivery('adsense');
      } else if (status === 'unfilled' || status === 'unfill-optimized') {
        setDelivery(placement ? 'fallback' : 'empty');
      }
    };

    const observer = new MutationObserver(applyStatus);
    observer.observe(ins, { attributes: true, attributeFilter: ['data-ad-status'] });

    let script = document.querySelector('script[data-watchvim-adsense-sdk="true"]');
    let createdScript = false;
    if (!script) {
      script = document.createElement('script');
      script.async = true;
      script.crossOrigin = 'anonymous';
      script.src = `https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=${encodeURIComponent(client)}`;
      script.dataset.watchvimAdsenseSdk = 'true';
      document.head.appendChild(script);
      createdScript = true;
    }

    const handleScriptError = () => setDelivery(placement ? 'fallback' : 'empty');
    script.addEventListener('error', handleScriptError);

    const pushTimer = window.setTimeout(() => {
      if (!ins.isConnected || ins.dataset.watchvimAdsensePushed === 'true') return;
      ins.dataset.watchvimAdsensePushed = 'true';
      try {
        window.adsbygoogle = window.adsbygoogle || [];
        window.adsbygoogle.push({});
      } catch (_) {
        setDelivery(placement ? 'fallback' : 'empty');
      }
    }, 75);

    const noResponseTimer = window.setTimeout(() => {
      const status = String(ins.dataset.adStatus || '').toLowerCase();
      if (!status && !ins.querySelector('iframe')) setDelivery(placement ? 'fallback' : 'empty');
    }, 8000);

    return () => {
      window.clearTimeout(pushTimer);
      window.clearTimeout(noResponseTimer);
      observer.disconnect();
      script.removeEventListener('error', handleScriptError);
      if (createdScript && !script.isConnected) script.remove();
      if (host.contains(ins)) host.replaceChildren();
    };
  }, [enabled, client, slot, adUnitPath, refreshDeclared, titleId, placement?.id, placement?.imageUrl]);

  const showFallback = delivery === 'fallback' && Boolean(placement);
  const showAdsense = delivery === 'loading' || delivery === 'adsense' || delivery === 'gam';
  const source = delivery === 'gam' ? 'gam' : delivery === 'adsense'
    ? 'adsense'
    : showFallback
      ? (placement?.source || 'internal')
      : delivery;

  return (
    <aside className="watchvim-companion-rail" aria-label="Advertisement">
      <span className="watchvim-companion-label">Advertisement</span>
      <div
        className="watchvim-companion-box"
        data-companion-source={source}
        data-companion-ready={String(delivery === 'adsense' || delivery === 'gam' || showFallback)}
        data-adsense-status={delivery}
      >
        <div
          ref={hostRef}
          className="watchvim-adsense-host"
          aria-hidden={showAdsense ? 'false' : 'true'}
          style={{ display: showAdsense ? 'block' : 'none', width: '100%', height: '100%' }}
        />
        {showFallback ? (
          <a className="watchvim-direct-companion" href={placement.clickUrl || 'https://watchvim.com'} target="_blank" rel="noopener noreferrer sponsored">
            <img src={placement.imageUrl} alt={placement.alt || 'Advertisement'} width="300" height="250" />
          </a>
        ) : null}
      </div>
    </aside>
  );
}

function PlaybackSurface({ media, backTo, backLabel, decision, catalog }) {
  const { preferences } = useConsent();
  const consentRef = useRef(preferences);
  consentRef.current = preferences;
  const { user } = useSession();
  const playerRef = useRef(null);
  const stageRef = useRef(null);
  const adContainerRef = useRef(null);
  const adControllerRef = useRef(null);
  const lastSavedSecondRef = useRef(-1);
  const lastCloudSavedSecondRef = useRef(-1);
  const completedBreaksRef = useRef(new Set());
  const breakpointsRef = useRef([]);
  const breakpointsDurationRef = useRef(0);
  const lastPlaybackTimeRef = useRef(0);
  const seekingRef = useRef(false);
  const breakInFlightRef = useRef(false);
  const fullscreenHistoryArmedRef = useRef(false);
  const [failed, setFailed] = useState(false);
  const [adConfig, setAdConfig] = useState(null);
  const [adRuntimeReady, setAdRuntimeReady] = useState(decision?.adMode !== 'avod');
  const [adPhase, setAdPhase] = useState(decision?.adMode === 'avod' ? 'preparing' : 'none');
  const [adEventState, setAdEventState] = useState('idle');
  const [adTelemetry, setAdTelemetry] = useState({ code: '', source: '', position: '' });
  const [adDisplay, setAdDisplay] = useState({ active: false, slotNumber: 0, slotTotal: MIDROLL_SLOT_COUNT, remainingSec: null });
  const [mobileFullscreen, setMobileFullscreen] = useState(false);
  const [prerollComplete, setPrerollComplete] = useState(decision?.adMode !== 'avod');
  const [midrollTimeline, setMidrollTimeline] = useState({ duration: 0, points: [] });
  const viewerScope = useMemo(() => viewerProgressScope(user), [user?.id]);
  const resume = useMemo(() => readPlaybackProgress(viewerScope, media.mediaKey), [viewerScope, media.mediaKey]);
  const isAvod = decision?.adMode === 'avod' && media.kind !== 'trailer';
  const showStartGate = isAvod && !prerollComplete && (adPhase === 'preparing' || adPhase === 'ready');
  const companionPlacements = adConfig?.advertising?.companionPlacements || [];
  const showCompanion = isAvod && (adConfig?.adsense?.enabled === true || companionPlacements.length > 0);
  const midrollMarkerStyle = useMemo(() => (
    midrollTimeline.points.length
      ? { '--watchvim-midroll-marker-background': midrollMarkerBackground(midrollTimeline.points, midrollTimeline.duration) }
      : undefined
  ), [midrollTimeline]);
  const midrollCuePercents = useMemo(() => {
    const duration = Number(midrollTimeline.duration) || 0;
    if (!duration) return [];
    return midrollTimeline.points
      .map((point) => Math.min(100, Math.max(0, (Number(point) / duration) * 100)))
      .filter((value) => Number.isFinite(value));
  }, [midrollTimeline]);
  const midrollCueSummary = useMemo(() => (
    midrollTimeline.points.length
      ? `${midrollTimeline.points.length} ad ${midrollTimeline.points.length === 1 ? 'break is' : 'breaks are'} marked on the timeline at ${midrollTimeline.points.map(formatCueTime).join(', ')}.`
      : ''
  ), [midrollTimeline]);

  useEffect(() => {
    setFailed(false);
    lastSavedSecondRef.current = -1;
    lastCloudSavedSecondRef.current = -1;
    completedBreaksRef.current = new Set();
    breakpointsRef.current = [];
    breakpointsDurationRef.current = 0;
    lastPlaybackTimeRef.current = 0;
    seekingRef.current = false;
    breakInFlightRef.current = false;
    setMidrollTimeline({ duration: 0, points: [] });
    setAdTelemetry({ code: '', source: '', position: '' });
    setAdDisplay({ active: false, slotNumber: 0, slotTotal: MIDROLL_SLOT_COUNT, remainingSec: null });
    setMobileFullscreen(false);
  }, [media.mediaKey, media.playbackId, isAvod]);

  useEffect(() => {
    if (adPhase === 'midroll') return;
    setAdDisplay((current) => current.active
      ? { active: false, slotNumber: 0, slotTotal: MIDROLL_SLOT_COUNT, remainingSec: null }
      : current);
  }, [adPhase]);

  useEffect(() => {
    if (!mobileFullscreen) return undefined;
    const previousBodyOverflow = document.body.style.overflow;
    const previousHtmlOverflow = document.documentElement.style.overflow;
    document.body.style.overflow = 'hidden';
    document.documentElement.style.overflow = 'hidden';
    document.documentElement.dataset.watchvimMobileFullscreen = 'true';

    const currentState = window.history.state || {};
    if (!currentState.__watchvimMobileFullscreen) {
      try {
        window.history.pushState(
          { ...currentState, __watchvimMobileFullscreen: true },
          '',
          window.location.href,
        );
        fullscreenHistoryArmedRef.current = true;
      } catch (_) {
        fullscreenHistoryArmedRef.current = false;
      }
    }

    const handleKey = (event) => {
      if (event.key === 'Escape') setMobileFullscreen(false);
    };
    const handlePopState = () => {
      fullscreenHistoryArmedRef.current = false;
      setMobileFullscreen(false);
    };
    const resizeAds = () => window.requestAnimationFrame(() => adControllerRef.current?.resize?.());
    window.addEventListener('keydown', handleKey);
    window.addEventListener('popstate', handlePopState);
    window.addEventListener('orientationchange', resizeAds);
    resizeAds();

    return () => {
      window.removeEventListener('keydown', handleKey);
      window.removeEventListener('popstate', handlePopState);
      window.removeEventListener('orientationchange', resizeAds);
      document.body.style.overflow = previousBodyOverflow;
      document.documentElement.style.overflow = previousHtmlOverflow;
      delete document.documentElement.dataset.watchvimMobileFullscreen;
      if (fullscreenHistoryArmedRef.current && window.history.state?.__watchvimMobileFullscreen) {
        fullscreenHistoryArmedRef.current = false;
        try { window.history.back(); } catch (_) {}
      }
      resizeAds();
    };
  }, [mobileFullscreen]);

  useEffect(() => {
    let cancelled = false;
    if (!isAvod) {
      setAdRuntimeReady(true);
      setPrerollComplete(true);
      setAdPhase('none');
      return undefined;
    }

    setAdRuntimeReady(false);
    setPrerollComplete(false);
    setAdPhase('preparing');
    setAdEventState('idle');

    Promise.resolve().then(async () => {
      const config = await fetchAdConfig();
      if (cancelled) return;
      setAdConfig(config);
      if (!config.enabled) {
        setAdPhase('ready');
        setAdRuntimeReady(true);
        return;
      }

      const controller = await createImaController({
        container: adContainerRef.current,
        sdkUrl: config.imaSdkUrl,
        getConsent: () => consentRef.current,
        getCurrentTime: () => Number(playerRef.current?.currentTime) || 0,
        pauseContent: () => {
          try { playerRef.current?.pause?.(); } catch (_) {}
        },
        onState: (detail) => {
          if (cancelled) return;
          const phase = String(detail?.phase || 'idle');
          const position = String(detail?.position || '');
          const slotNumber = Math.max(0, Math.floor(Number(detail?.slotNumber) || 0));
          const slotTotal = Math.max(MIDROLL_SLOT_COUNT, Math.floor(Number(detail?.slotTotal) || 0));
          setAdEventState(phase);
          setAdTelemetry({
            code: String(detail?.code || ''),
            source: String(detail?.tagSource || ''),
            position,
          });
          if (position === 'midroll') {
            setAdDisplay((current) => ({
              active: true,
              slotNumber: slotNumber || current.slotNumber || 1,
              slotTotal,
              remainingSec: Number.isFinite(Number(detail?.remainingSec))
                ? Math.max(0, Math.ceil(Number(detail.remainingSec)))
                : (detail?.event === 'requesting' ? null : current.remainingSec),
            }));
          }
        },
      });
      if (cancelled) {
        controller.destroy();
        return;
      }
      adControllerRef.current = controller;
      setAdPhase('ready');
      setAdRuntimeReady(true);
    }).catch(() => {
      if (cancelled) return;
      adControllerRef.current = null;
      setAdPhase('ready');
      setAdRuntimeReady(true);
      setAdEventState('unavailable');
    });

    const onResize = () => adControllerRef.current?.resize?.();
    window.addEventListener('resize', onResize);
    window.addEventListener('orientationchange', onResize);
    return () => {
      cancelled = true;
      window.removeEventListener('resize', onResize);
      window.removeEventListener('orientationchange', onResize);
      adControllerRef.current?.destroy?.();
      adControllerRef.current = null;
    };
  }, [isAvod, media.mediaKey]);

  useEffect(() => {
    return () => {
      const player = playerRef.current;
      if (!player || media.kind === 'trailer') return;
      try {
        const seconds = Number(player.currentTime) || 0;
        const duration = Number(player.duration) || 0;
        savePlaybackProgress(viewerScope, media.mediaKey, seconds, duration);
        if (user?.id && seconds >= 1) saveCloudProgress(user, media, seconds, duration).catch(() => {});
      } catch (_) {}
    };
  }, [viewerScope, media.mediaKey, media.kind, user?.id]);

  useEffect(() => {
    if (!isAvod || !adConfig) return;
    refreshBreakpoints(playerRef.current);
  }, [adConfig, isAvod, decision?.policy, catalog, media.mediaKey]);

  function saveFromPlayer(player, force = false) {
    if (!player || media.kind === 'trailer') return;
    const seconds = Number(player.currentTime) || 0;
    const duration = Number(player.duration) || 0;
    const whole = Math.floor(seconds);
    if (!force && whole - lastSavedSecondRef.current < SAVE_INTERVAL_SECONDS) return;
    lastSavedSecondRef.current = whole;
    savePlaybackProgress(viewerScope, media.mediaKey, seconds, duration);

    if (user?.id && (force || whole - lastCloudSavedSecondRef.current >= CLOUD_SAVE_INTERVAL_SECONDS)) {
      lastCloudSavedSecondRef.current = whole;
      saveCloudProgress(user, media, seconds, duration).catch(() => {});
    }
  }

  function refreshBreakpoints(player) {
    if (!isAvod || !adConfig?.enabled) return;
    const duration = Number(player?.duration) || Number(media.durationHint) || 0;
    if (!Number.isFinite(duration) || duration <= 0) return;
    const points = midrollBreakpoints(duration, decision?.policy, adConfig, catalog);
    const unchanged = breakpointsDurationRef.current === duration
      && points.length === breakpointsRef.current.length
      && points.every((point, index) => point === breakpointsRef.current[index]);
    if (unchanged) return;
    breakpointsDurationRef.current = duration;
    breakpointsRef.current = points;
    setMidrollTimeline({ duration, points });
  }

  function handleLoadedMetadata(event) {
    const player = event.currentTarget;
    refreshBreakpoints(player);
    if (media.kind === 'trailer' || !resume?.seconds) return;
    const duration = Number(player.duration) || Number(resume.duration) || 0;
    if (duration > 0 && resume.seconds >= duration - 20) {
      clearPlaybackProgress(viewerScope, media.mediaKey);
      if (user?.id) clearCloudProgress(user, media.mediaKey).catch(() => {});
      return;
    }
    try {
      player.currentTime = resume.seconds;
      lastPlaybackTimeRef.current = Number(resume.seconds) || 0;
    } catch (_) {}
  }

  async function runAdPod(position) {
    const controller = adControllerRef.current;
    const config = adConfig;
    if (!isAvod || !config?.enabled || !controller) return { delivered: false };

    if (position === 'midroll') {
      const result = await runFixedMidrollPod({
        controller,
        config,
        titleId: media.titleId,
        contentDuration: Number(playerRef.current?.duration) || 0,
        contentTitle: media.title,
        sleep,
      });
      try {
        window.__WV = window.__WV || {};
        window.__WV.__lastMidrollDelivery = result;
      } catch (_) {}
      return result;
    }

    const count = config.prerollPodCount;
    const podBudget = config.vastLoadTimeoutMs + config.maxAdMs + 5000;
    const startedAt = Date.now();
    let delivered = 0;

    for (let slot = 0; slot < count; slot += 1) {
      let remainingMs = podBudget - (Date.now() - startedAt);
      if (remainingMs <= 0) break;
      const candidates = adTagCandidates({
        config,
        titleId: media.titleId,
        position,
        includeDirect: true,
        allowGoogle: consentRef.current?.decided === true && consentRef.current?.advertising === true,
      });
      let slotConsumed = false;
      for (const tagUrl of candidates) {
        remainingMs = podBudget - (Date.now() - startedAt);
        if (remainingMs <= 0) break;
        try {
          const outcome = await controller.playTag(tagUrl, {
            muted: config.startMuted,
            loadTimeoutMs: Math.min(config.vastLoadTimeoutMs, Math.max(1000, remainingMs)),
            maxAdMs: config.maxAdMs,
            requestBudgetMs: remainingMs,
            contentDuration: Number(playerRef.current?.duration) || 0,
            contentTitle: media.title,
            position,
            slotId: 'preroll-slot-1',
            slotNumber: 1,
            slotTotal: 1,
          });
          if (outcome?.started === true) delivered += 1;
          if (outcome?.started === true || outcome?.consumed === true) {
            slotConsumed = true;
            break;
          }
        } catch (_) {}
      }
      if (!slotConsumed) break;
    }
    return { delivered: delivered > 0, count: delivered, started: delivered, elapsedMs: Date.now() - startedAt, budgetMs: podBudget };
  }

  async function beginAvodPlayback() {
    if (!isAvod || !adRuntimeReady || breakInFlightRef.current) return;
    breakInFlightRef.current = true;
    try {
      const controller = adControllerRef.current;
      if (controller) {
        try { controller.initialize(); } catch (_) {}
      }
      setAdPhase('preroll');
      try { playerRef.current?.pause?.(); } catch (_) {}
      await runAdPod('preroll');
    } finally {
      setPrerollComplete(true);
      setAdPhase('content');
      breakInFlightRef.current = false;
      try { await playerRef.current?.play?.(); } catch (_) {}
    }
  }

  async function runMidroll(index) {
    if (!isAvod || !prerollComplete || breakInFlightRef.current) return;
    completedBreaksRef.current.add(index);
    breakInFlightRef.current = true;
    const player = playerRef.current;
    try {
      try { player?.pause?.(); } catch (_) {}
      setAdPhase('midroll');
      setAdDisplay({ active: true, slotNumber: 1, slotTotal: MIDROLL_SLOT_COUNT, remainingSec: null });
      await runAdPod('midroll');
    } finally {
      setAdPhase('content');
      setAdDisplay({ active: false, slotNumber: 0, slotTotal: MIDROLL_SLOT_COUNT, remainingSec: null });
      breakInFlightRef.current = false;
      lastPlaybackTimeRef.current = Number(player?.currentTime) || 0;
      try { await player?.play?.(); } catch (_) {}
    }
  }

  function handleTimeUpdate(event) {
    const player = event.currentTarget;
    saveFromPlayer(player);
    if (!isAvod || !prerollComplete || seekingRef.current || breakInFlightRef.current) return;
    refreshBreakpoints(player);
    if (!breakpointsRef.current.length) return;

    const now = Number(player.currentTime) || 0;
    const last = Number(lastPlaybackTimeRef.current) || 0;
    if (now < last - 3) {
      lastPlaybackTimeRef.current = now;
      return;
    }
    const dueIndex = breakpointsRef.current.findIndex((point, index) => (
      !completedBreaksRef.current.has(index) && last < point && now >= point
    ));
    lastPlaybackTimeRef.current = now;
    if (dueIndex >= 0) void runMidroll(dueIndex);
  }

  function handleSeeking(event) {
    seekingRef.current = true;
    saveFromPlayer(event.currentTarget, true);
  }

  function handleSeeked(event) {
    const player = event.currentTarget;
    refreshBreakpoints(player);
    const now = Number(player.currentTime) || 0;
    breakpointsRef.current.forEach((point, index) => {
      if (point <= now) completedBreaksRef.current.add(index);
    });
    lastPlaybackTimeRef.current = now;
    seekingRef.current = false;
    saveFromPlayer(player, true);
  }

  function handleEnded() {
    adControllerRef.current?.contentComplete?.();
    clearPlaybackProgress(viewerScope, media.mediaKey);
    if (user?.id) clearCloudProgress(user, media.mediaKey).catch(() => {});
  }

  if (!media.playbackId) {
    return (
      <section className="playback-page playback-unavailable" data-playback-kind={media.kind} data-playback-key={media.mediaKey} data-playback-id="">
        <div className="playback-fallback" style={media.poster ? { backgroundImage: `url(${media.poster})` } : undefined}>
          <div className="playback-fallback-shade" />
          <div className="playback-fallback-copy">
            <span className="eyebrow">WatchVIM</span>
            <h1>{media.title || 'Playback unavailable'}</h1>
            <p>This title does not currently have a published playback asset.</p>
            <NavLink className="playback-back-button" to={backTo}>{backLabel}</NavLink>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section
      className="playback-page"
      data-playback-kind={media.kind}
      data-playback-key={media.mediaKey}
      data-playback-id={media.playbackId}
      data-resume-seconds={resume?.seconds || 0}
      data-ad-mode={isAvod ? 'avod' : 'none'}
      data-avod-phase={isAvod ? adPhase : 'none'}
      data-preroll-complete={isAvod ? String(prerollComplete) : 'true'}
      data-midroll-marker-count={isAvod ? midrollTimeline.points.length : 0}
      data-midroll-cue-seconds={isAvod ? midrollTimeline.points.join(',') : ''}
      data-midroll-marker-background={isAvod ? midrollMarkerStyle?.['--watchvim-midroll-marker-background'] || '' : ''}
      data-midroll-cue-label={isAvod ? midrollCueSummary : ''}
      data-ad-event-state={isAvod ? adEventState : 'idle'}
      data-ad-error-code={isAvod ? adTelemetry.code : ''}
      data-ad-source={isAvod ? adTelemetry.source : ''}
      data-ad-position={isAvod ? adTelemetry.position : ''}
      data-watchvim-mobile-fullscreen={String(mobileFullscreen)}
    >
      <div className="playback-heading">
        <NavLink className="back-link" to={backTo}>← {backLabel}</NavLink>
        <div>
          {media.seriesTitle ? <span>{media.seriesTitle}</span> : null}
          <h1>{media.title}</h1>
          {resume?.seconds && media.kind !== 'trailer' ? <p>Resuming from {Math.floor(resume.seconds / 60)}m {Math.floor(resume.seconds % 60)}s</p> : null}
        </div>
      </div>

      <div className={showCompanion ? 'playback-ad-layout has-companion' : 'playback-ad-layout'}>
        <div ref={stageRef} className={mobileFullscreen ? 'playback-stage is-watchvim-mobile-fullscreen' : 'playback-stage'}>
        {!failed ? (
          <React.Suspense fallback={<div className="playback-player-loading">Loading player…</div>}>
            <MuxPlayer
              ref={playerRef}
              className="watchvim-content-player"
              style={midrollMarkerStyle}
              playbackId={media.playbackId}
              tokens={media.tokens}
              envKey={import.meta.env.VITE_WATCHVIM_MUX_DATA_ENV_KEY || undefined}
              autoPlay={!isAvod}
              playsInline
              preload="metadata"
              poster={media.poster || undefined}
              metadata={{
                video_id: media.mediaKey,
                video_title: media.title,
                viewer_user_id: user?.id || 'guest',
                player_name: 'WatchVIM V3 Content Player',
                video_series: media.seriesTitle || undefined,
                page_type: 'watchpage',
                sub_property_id: 'watchvim-web',
                video_stream_type: 'on-demand',
              }}
              onLoadedMetadata={handleLoadedMetadata}
              onTimeUpdate={handleTimeUpdate}
              onSeeking={handleSeeking}
              onSeeked={handleSeeked}
              onPause={(event) => saveFromPlayer(event.currentTarget, true)}
              onEnded={handleEnded}
              onError={() => setFailed(true)}
            />
            {midrollCueSummary ? <p className="watchvim-ad-cue-summary" aria-live="polite">{midrollCueSummary}</p> : null}
          </React.Suspense>
        ) : (
          <div className="playback-fallback inline" style={media.poster ? { backgroundImage: `url(${media.poster})` } : undefined}>
            <div className="playback-fallback-shade" />
            <div className="playback-fallback-copy">
              <h2>We couldn’t start this video.</h2>
              <p>The artwork remains available while the media connection is unavailable.</p>
              <button type="button" onClick={() => setFailed(false)}>Try Again</button>
            </div>
          </div>
        )}

        {isAvod ? (
          <div
            ref={adContainerRef}
            className={adPhase === 'preroll' || adPhase === 'midroll' ? 'watchvim-ad-layer is-active' : 'watchvim-ad-layer'}
            data-ad-event-state={adEventState}
            aria-hidden={adPhase === 'preroll' || adPhase === 'midroll' ? 'false' : 'true'}
          />
        ) : null}

        {showStartGate ? (
          <div className="avod-start-gate" data-avod-ready={String(adRuntimeReady)}>
            <div className="avod-start-card">
              <span className="eyebrow">Watch Free with Ads</span>
              <h2>{media.title}</h2>
              <p>{adRuntimeReady ? 'A short ad break will play before your video.' : 'Preparing the ad-supported player…'}</p>
              <button type="button" disabled={!adRuntimeReady} onClick={beginAvodPlayback}>
                {adRuntimeReady ? 'Play with Ads' : 'Preparing…'}
              </button>
            </div>
          </div>
        ) : null}

        {isAvod && adPhase === 'preroll' ? (
          <div className="avod-ad-label" aria-live="polite">Advertisement</div>
        ) : null}

        {isAvod && adPhase === 'midroll' && adDisplay.active ? (
          <div
            className="avod-midroll-status"
            aria-live="polite"
            data-ad-slot={adDisplay.slotNumber || 1}
            data-ad-slot-total={adDisplay.slotTotal || MIDROLL_SLOT_COUNT}
          >
            Ad {adDisplay.slotNumber || 1} of {adDisplay.slotTotal || MIDROLL_SLOT_COUNT}
            {Number.isFinite(Number(adDisplay.remainingSec)) ? ` · ${formatAdCountdown(adDisplay.remainingSec)}` : ''}
          </div>
        ) : null}

        {mobileFullscreen && isAvod && midrollCuePercents.length ? (
          <div className="watchvim-fullscreen-midroll-track" aria-hidden="true">
            {midrollCuePercents.map((percent, index) => (
              <span
                key={`${midrollTimeline.points[index]}-${index}`}
                className={completedBreaksRef.current.has(index) ? 'is-complete' : ''}
                style={{ left: `${percent}%` }}
              />
            ))}
          </div>
        ) : null}

        <button
          type="button"
          className={mobileFullscreen ? 'watchvim-mobile-fullscreen-toggle is-exit' : 'watchvim-mobile-fullscreen-toggle'}
          aria-label={mobileFullscreen ? 'Back to Watch page' : 'Enter full screen'}
          aria-pressed={mobileFullscreen}
          onClick={() => setMobileFullscreen((current) => !current)}
        >
          {mobileFullscreen ? (
            <>
              <span aria-hidden="true">←</span>
              <span className="watchvim-mobile-fullscreen-label">Back to Watch</span>
            </>
          ) : '⛶'}
        </button>
        </div>

        {showCompanion ? <CompanionAd adsense={adConfig.adsense} placements={companionPlacements} titleId={media.titleId} /> : null}
      </div>
    </section>
  );
}

export function TitlePlaybackPage({ kind = 'content' }) {
  const { titleId } = useParams();
  const { catalog } = useCatalog();
  const { user } = useSession();
  const access = useAccess();
  const item = findTitle(catalog, titleId);
  const media = item ? { ...resolveTitlePlayback(item, kind), titleId: item.id, durationHint: mediaDurationHint(item) } : null;
  const decision = item ? access.titleAccess(item, kind) : null;
  const backTo = item ? (item.kind === 'series' ? `/series/${encodeURIComponent(item.id)}` : `/title/${encodeURIComponent(item.id)}`) : '/home';

  return (
    <PlaybackState>
      {item && media ? (
        kind !== 'trailer' && user && access.loading ? (
          <section className="playback-state" aria-busy="true"><p>Verifying your WatchVIM access…</p></section>
        ) : decision?.allowed ? (
          <ProtectedPlayback item={item} media={media} backTo={backTo} backLabel={`Back to ${item.title}`} decision={decision} catalog={catalog} />
        ) : (
          <AccessBlocked decision={decision} title={item.title} backTo={backTo} accessError={access.error} />
        )
      ) : <section className="playback-state playback-error"><h1>Title not found.</h1><NavLink to="/home">Return Home</NavLink></section>}
    </PlaybackState>
  );
}

export function EpisodePlaybackPage() {
  const { seriesId, season, episode } = useParams();
  const { catalog } = useCatalog();
  const { user } = useSession();
  const access = useAccess();
  const series = findTitle(catalog, seriesId);
  const item = findEpisode(series, season, episode);
  const media = series && item ? { ...resolveEpisodePlayback(series, item), titleId: series.id, durationHint: mediaDurationHint(item) } : null;
  const decision = series && item ? access.episodeAccess(series, item) : null;
  const backTo = series && item ? `/episode/${encodeURIComponent(series.id)}/${item.season}/${item.episode}` : '/home';

  return (
    <PlaybackState>
      {series && item && media ? (
        user && access.loading ? (
          <section className="playback-state" aria-busy="true"><p>Verifying your WatchVIM access…</p></section>
        ) : decision?.allowed ? (
          <ProtectedPlayback item={item} parent={series} media={media} backTo={backTo} backLabel={`Back to ${item.title}`} decision={decision} catalog={catalog} />
        ) : (
          <AccessBlocked decision={decision} title={item.title} backTo={backTo} accessError={access.error} />
        )
      ) : <section className="playback-state playback-error"><h1>Episode not found.</h1><NavLink to="/home">Return Home</NavLink></section>}
    </PlaybackState>
  );
}
